// The Awaiting invoice list's filters, the service query they make, and the words of each row.
// Filters are non-personal, so they may sit in the URL; a phone or a frame number never does.

export const STATUS_FILTERS = ["awaiting_invoice", "expired", "all"];
// "0" is due today or already overdue; the service takes 0 to 366.
export const DUE_FILTERS = ["0", "2", "7", "14", "30"];
export const SORTS = ["due_asc", "due_desc"];
export const PAGE_SIZE = 25;

// why: two days is the last chance to remind the rider before the window closes; a named number so
// the badge and its tooltip agree.
export const DUE_SOON_DAYS = 2;

export const DEFAULT_FILTERS = {
  status: "awaiting_invoice",
  dueWithinDays: "",
  overdue: false,
  sort: "due_asc",
};

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

// The due and overdue filters match awaiting rows only, so the expired list never carries them.
export const filtersFromParams = (params) => {
  const status = oneOf(params.get("status"), STATUS_FILTERS, DEFAULT_FILTERS.status);
  const expired = status === "expired";
  return {
    status,
    dueWithinDays: expired ? "" : oneOf(params.get("due"), DUE_FILTERS, ""),
    overdue: !expired && params.get("overdue") === "1",
    sort: oneOf(params.get("sort"), SORTS, DEFAULT_FILTERS.sort),
  };
};

// Only the values that differ from the defaults, so the plain list has a plain URL.
export const filtersToParams = (filters) => {
  const out = {};
  if (filters.status !== DEFAULT_FILTERS.status) out.status = filters.status;
  if (filters.dueWithinDays) out.due = filters.dueWithinDays;
  if (filters.overdue) out.overdue = "1";
  if (filters.sort !== DEFAULT_FILTERS.sort) out.sort = filters.sort;
  return out;
};

// The query keys of the awaiting invoice list. The service refuses unknown keys, so only set ones go.
export const listQuery = (filters, cursor, limit) => {
  const out = { status: filters.status, sort: filters.sort, limit };
  if (filters.dueWithinDays) out.dueWithinDays = filters.dueWithinDays;
  if (filters.overdue) out.overdue = "true";
  if (cursor) out.cursor = cursor;
  return out;
};

const isAwaiting = (item) => item.status === "awaiting_invoice";

// The days left column. invoiceDaysLeft is 0 on the due date, after it and once expired, so the
// status and the overdue flag decide what a 0 means.
export const daysLeftLabel = (item) => {
  if (!isAwaiting(item)) {
    return "-";
  }
  if (item.overdue) {
    return "Overdue";
  }
  const days = item.invoiceDaysLeft;
  if (!Number.isInteger(days) || days < 0) {
    return "-";
  }
  if (days === 0) {
    return "Due today";
  }
  return `${days} ${days === 1 ? "day" : "days"}`;
};

// An awaiting row whose window closes within DUE_SOON_DAYS. Overdue rows have their own badge.
export const isDueSoon = (item) =>
  isAwaiting(item) &&
  !item.overdue &&
  Number.isInteger(item.invoiceDaysLeft) &&
  item.invoiceDaysLeft >= 0 &&
  item.invoiceDaysLeft <= DUE_SOON_DAYS;

// The number on a status chip, from the first page's counts; null shows no number.
export const statusCount = (counts, status) => {
  const byStatus = counts?.byStatus;
  if (!byStatus) {
    return null;
  }
  if (status === "all") {
    const { awaiting_invoice: awaiting, expired } = byStatus;
    return Number.isInteger(awaiting) && Number.isInteger(expired) ? awaiting + expired : null;
  }
  return Number.isInteger(byStatus[status]) ? byStatus[status] : null;
};
